import { MOBILE_BREAKPOINT } from "@/hooks/use-mobile.tsx";
import { useTaskFlash } from "@/shared/userTaskFlash.ts";
import { type AnimationEvent, useCallback, useEffect, useRef } from "react";

/**
 * Name of the `@keyframes` in `index.css` that `.animate-task-flash` runs, so
 * a row can tell its own animation ending from any other.
 */
const TASK_FLASH_ANIMATION = "task-flash";

/**
 * A row the list has just been sent to: it scrolls itself into view and wears
 * the glow once, whichever shape it is drawn in.
 *
 * Arriving is a link, or a chip the first time, which `focused` says; and
 * every press after that, which only the glow's flag can say, since the route
 * names the same task each time and the row clears the flag when the glow
 * ends. Shared by the card and the compact line so a link to a task below
 * the fold lands on it in either view.
 */
export const useTaskArrival = <T extends HTMLElement>(taskId: string, focused: boolean) => {
  const ref = useRef<T>(null);
  const { flash, clearFlash } = useTaskFlash(taskId);
  const scrollSelfIntoView = useCallback(() => {
    // Top of the view on a phone, where centring a tall card can leave its
    // actions below the fold; centred where there is room for the rows around
    // it to give it context.
    const narrow = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
    ref.current?.scrollIntoView({ block: narrow ? "start" : "center" });
  }, []);
  useEffect(() => {
    if (focused) scrollSelfIntoView();
  }, [focused, scrollSelfIntoView]);
  useEffect(() => {
    if (flash) scrollSelfIntoView();
  }, [flash, scrollSelfIntoView]);
  // Matched by name so an unrelated animation ending on the row — a hover
  // transition, a sibling's — cannot clear the flag early.
  const onAnimationEnd = useCallback(
    (event: AnimationEvent) => {
      if (event.animationName === TASK_FLASH_ANIMATION) clearFlash();
    },
    [clearFlash],
  );
  return { ref, flash, onAnimationEnd };
};
